import React, { useState } from 'react';
import { useDrag } from '@use-gesture/react';
import { useSpring, animated } from '@react-spring/web';
import { Tag, Edit2, Trash2, Pin, PinOff } from 'lucide-react';
import type { Note } from '../../hooks/useNotes';
import type { Role } from '../../types';

interface NoteCardProps {
  id: string;
  content: Note['content'];
  tags: string[];
  isPinned: boolean;
  color: string;
  role: Exclude<Role, 'all'>;
  onPin: (id: string) => void;
  onEdit: (id: string) => void;
  onView: (id: string) => void;
  onDelete: (id: string) => void;
  onTagClick: (tag: string) => void;
}

export function NoteCard({
  id,
  content,
  tags = [], // Provide default empty array
  isPinned,
  color,
  role,
  onPin,
  onEdit,
  onView,
  onDelete,
  onTagClick,
}: NoteCardProps) {
  const [{ x }, api] = useSpring(() => ({ x: 0 }));

  const bind = useDrag(
    ({ down, movement: [mx], cancel }) => {
      // Only handle horizontal drags
      const swipeThreshold = 100;

      // If swiped far enough and released, delete
      if (!down && Math.abs(mx) > swipeThreshold) {
        onDelete(id);
        return;
      }

      // Spring animation
      api.start({
        x: down ? Math.min(0, mx) : 0,
        immediate: down
      });
    },
    { axis: 'x', threshold: 10 }
  );

  return (
    <animated.div
      className="relative overflow-hidden"
    >
      {/* Delete button background */}
      <div className="absolute inset-y-0 right-0 w-20 bg-red-500 flex items-center justify-center">
        <Trash2 className="w-5 h-5 text-white" />
      </div>

      {/* Card content */}
      <animated.div
        {...bind()}
        style={{ x, touchAction: 'pan-y' }}
        onClick={() => onView(id)}
        className="relative bg-[#141414] p-4 rounded-xl transition-all hover:ring-1 hover:ring-white/10 text-white cursor-pointer hover:bg-[#141414]/80 duration-300"
      >
        <div>
          <div className="flex justify-between items-start">
            <h3 className="font-medium text-base">
              {content.title}
            </h3>
            <div className="flex items-center gap-0" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => onPin(id)}
                className="p-3 hover:bg-black/20 rounded-xl transition-colors"
              >
                {isPinned ? (
                  <PinOff className="w-5 h-5" />
                ) : (
                  <Pin className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={() => onEdit(id)}
                className="p-3 hover:bg-black/20 rounded-xl transition-colors"
              >
                <Edit2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="text-sm text-gray-400 mt-0.5">
            {content.text.split('\n')[0]}
          </div>

          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-black/20 rounded-full text-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTagClick(tag);
                  }}
                >
                  <Tag className="w-4 h-4" />
                  {tag}
                </span>
              ))}
              </div>
          )}
        </div>
      </animated.div>
    </animated.div>
  );
}